import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  FiRefreshCw,
  FiSearch,
  FiFilter,
  FiX,
  FiCheckCircle,
  FiXCircle,
  FiLoader,
  FiClock,
  FiAlertTriangle,
  FiFileText,
} from 'react-icons/fi';
import api from '../services/api';
import { toast } from 'react-toastify';
import { formatDateTime } from '../utils/dateFormat';
import { CRUD_UI } from '../components/crudUi';
import Pagination from '../components/Pagination';

// ─── Status badge helper ────────────────────────────────────────────────────

const STATUS_CONFIG = {
  completed: {
    label: 'Hoàn thành',
    bg: 'bg-emerald-100',
    text: 'text-emerald-700',
    icon: FiCheckCircle,
  },
  failed: {
    label: 'Thất bại',
    bg: 'bg-red-100',
    text: 'text-red-700',
    icon: FiXCircle,
  },
  running: {
    label: 'Đang chạy',
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    icon: FiLoader,
  },
  pending: {
    label: 'Đang chờ',
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    icon: FiClock,
  },
};

const getStatusBadge = (status) => {
  const cfg = STATUS_CONFIG[status] || {
    label: status,
    bg: 'bg-slate-100',
    text: 'text-slate-700',
    icon: FiAlertTriangle,
  };
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${cfg.bg} ${cfg.text}`}
    >
      <Icon
        className={`text-sm ${status === 'running' ? 'animate-spin' : ''}`}
      />
      {cfg.label}
    </span>
  );
};

const parseLogValue = (log) => {
  if (!log) return { parsed: null, raw: '' };

  if (typeof log === 'object') {
    return {
      parsed: log,
      raw: JSON.stringify(log, null, 2),
    };
  }

  const raw = String(log);
  try {
    const parsed = JSON.parse(raw);
    return {
      parsed,
      raw: JSON.stringify(parsed, null, 2),
    };
  } catch {
    return { parsed: null, raw };
  }
};

const buildLogInsight = (log) => {
  const { parsed, raw } = parseLogValue(log);
  const jsonRaw = parsed && typeof parsed === 'object'
    ? JSON.stringify(parsed, null, 2)
    : parsed !== null
    ? JSON.stringify({ value: parsed }, null, 2)
    : raw
    ? JSON.stringify({ message: raw }, null, 2)
    : JSON.stringify({}, null, 2);

  if (!raw) {
    return {
      tone: 'neutral',
      summary: '-',
      meta: [],
      raw: '-',
      jsonRaw,
    };
  }

  if (!parsed || typeof parsed !== 'object') {
    return {
      tone: 'neutral',
      summary: raw.replace(/\s+/g, ' ').slice(0, 120),
      meta: [],
      raw,
      jsonRaw,
    };
  }

  const summary =
    parsed.error ||
    parsed.message ||
    (parsed.maxRetriesExceeded
      ? 'Đồng bộ thất bại sau khi vượt quá số lần retry'
      : parsed.success
      ? 'Đồng bộ thành công'
      : 'Chi tiết log đồng bộ');

  const tone = parsed.error || parsed.maxRetriesExceeded
    ? 'error'
    : parsed.success
    ? 'success'
    : 'info';

  const meta = [];
  if (parsed.studyId) meta.push({ key: 'Study', value: String(parsed.studyId) });
  if (parsed.retryCount !== undefined) meta.push({ key: 'Retry', value: String(parsed.retryCount) });
  if (parsed.statusCode) meta.push({ key: 'Code', value: String(parsed.statusCode) });
  if (parsed.timestamp) meta.push({ key: 'At', value: formatDateTime(parsed.timestamp) || String(parsed.timestamp) });

  return {
    tone,
    summary,
    meta,
    raw,
    jsonRaw,
  };
};

// ─── Log cell ────────────────────────────────────────────────────────────────

const LogCell = ({ log, onOpen }) => {
  const insight = useMemo(() => buildLogInsight(log), [log]);

  const toneClass =
    insight.tone === 'error'
      ? 'text-red-700 bg-red-50 border-red-100'
      : insight.tone === 'success'
      ? 'text-emerald-700 bg-emerald-50 border-emerald-100'
      : 'text-slate-700 bg-slate-50 border-slate-200';

  if (!log) return <span className="text-slate-400">-</span>;

  return (
    <div className="space-y-1">
      <button
        onClick={() => onOpen(insight)}
        className={`w-full text-left inline-flex items-center gap-1 px-2 py-1.5 border rounded text-xs ${toneClass}`}
        title={insight.summary}
      >
        <span className="truncate">{insight.summary}</span>
      </button>

      {insight.meta.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {insight.meta.map((item) => (
            <span
              key={`${item.key}-${item.value}`}
              className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px]"
              title={`${item.key}: ${item.value}`}
            >
              {item.key}: {item.value}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Duration helper ──────────────────────────────────────────────────────────

const formatDuration = (startedAt, finishedAt) => {
  if (!startedAt) return '-';
  const start = new Date(startedAt);
  const end = finishedAt ? new Date(finishedAt) : new Date();
  const ms = end - start;
  if (ms < 0) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
};

// ─── Main component ───────────────────────────────────────────────────────────

const SyncJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');

  // Pagination (server-side from API, client-side paging from filtered list)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedLog, setSelectedLog] = useState(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchJobs = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        else setRefreshing(true);

        const params = { limit: 200, page: 1 };
        if (filterStatus) params.status = filterStatus;
        if (filterType) params.type = filterType;

        const res = await api.get('/admin/jobs', { params });
        setJobs(res.data.data || []);
      } catch (err) {
        toast.error(
          err.response?.data?.error?.message || 'Không thể tải danh sách sync jobs'
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [filterStatus, filterType]
  );

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterStatus, filterType]);

  // ── Derived data ───────────────────────────────────────────────────────────

  const filteredJobs = useMemo(() => {
    if (!jobs.length) return [];
    return jobs.filter((j) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        j.patient_name?.toLowerCase().includes(q) ||
        j.patient_id?.toLowerCase().includes(q) ||
        j.site_name?.toLowerCase().includes(q) ||
        j.exam_id?.toLowerCase().includes(q) ||
        j.id?.toLowerCase().includes(q)
      );
    });
  }, [jobs, search]);

  const paginatedJobs = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredJobs.slice(start, start + itemsPerPage);
  }, [filteredJobs, currentPage, itemsPerPage]);

  const summary = useMemo(() => {
    const counts = { completed: 0, failed: 0, running: 0, pending: 0 };
    jobs.forEach((j) => {
      if (counts[j.status] !== undefined) counts[j.status]++;
    });
    return counts;
  }, [jobs]);

  const handleItemsPerPageChange = useCallback((v) => {
    setItemsPerPage(v);
    setCurrentPage(1);
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className={CRUD_UI.loadingWrap}>
        <div className={CRUD_UI.loadingSpinner} />
        <p className="text-slate-500 text-sm">Đang tải danh sách sync jobs...</p>
      </div>
    );
  }

  return (
    <div className={CRUD_UI.page}>
      {/* Filters */}
      <div className={`${CRUD_UI.filterCard} shrink-0`}>
        <div className={`${CRUD_UI.filterHeader} justify-between flex-wrap gap-2`}>
          <div className="flex items-center gap-2">
            <FiFilter className="text-slate-500 text-xl" />
            <span className="text-sm font-semibold text-slate-900">
              Tìm kiếm và bộ lọc
            </span>
          </div>
          <button
            onClick={() => fetchJobs(true)}
            disabled={refreshing}
            className={CRUD_UI.secondaryButton}
            title="Làm mới"
          >
            <FiRefreshCw className={`text-base ${refreshing ? 'animate-spin' : ''}`} />
            Làm mới
          </button>
        </div>
        <div className="flex gap-4 flex-wrap items-end">
          {/* Search */}
          <div className={CRUD_UI.filterFieldWide}>
            <label className={CRUD_UI.filterLabel}>Tìm kiếm</label>
            <div className={CRUD_UI.searchWrap}>
              <FiSearch className={CRUD_UI.searchIcon} />
              <input
                type="text"
                className={CRUD_UI.searchInput}
                placeholder="Tìm theo tên / mã bệnh nhân, cơ sở, job ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  className={CRUD_UI.searchClearButton}
                  onClick={() => setSearch('')}
                  title="Xóa tìm kiếm"
                >
                  <FiX className="text-base" />
                </button>
              )}
            </div>
          </div>

          {/* Status filter */}
          <div className={CRUD_UI.filterField}>
            <label className={CRUD_UI.filterLabel}>Trạng thái</label>
            <select
              className={CRUD_UI.filterSelect}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">Tất cả trạng thái</option>
              <option value="completed">Hoàn thành</option>
              <option value="failed">Thất bại</option>
              <option value="running">Đang chạy</option>
              <option value="pending">Đang chờ</option>
            </select>
          </div>

          {/* Type filter */}
          <div className={CRUD_UI.filterField}>
            <label className={CRUD_UI.filterLabel}>Loại job</label>
            <select
              className={CRUD_UI.filterSelect}
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">Tất cả loại</option>
              <option value="push">Push</option>
              <option value="pull">Pull</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className={`${CRUD_UI.tableViewportShell} min-h-0`}>
        <div className={CRUD_UI.tableViewportScroll}>
          <table className={CRUD_UI.table}>
            <thead className={CRUD_UI.tableHead}>
              <tr>
                <th className={CRUD_UI.tableHeaderCell}>#</th>
                <th className={CRUD_UI.tableHeaderCell}>Trạng thái</th>
                <th className={CRUD_UI.tableHeaderCell}>Loại</th>
                <th className={CRUD_UI.tableHeaderCell}>Bệnh nhân</th>
                <th className={CRUD_UI.tableHeaderCell}>Mã BN</th>
                <th className={CRUD_UI.tableHeaderCell}>Cơ sở</th>
                <th className={CRUD_UI.tableHeaderCell}>Bắt đầu</th>
                <th className={CRUD_UI.tableHeaderCell}>Kết thúc</th>
                <th className={CRUD_UI.tableHeaderCell}>Thời gian</th>
                <th className={CRUD_UI.tableHeaderCell}>Log</th>
              </tr>
            </thead>
            <tbody>
              {paginatedJobs.length === 0 ? (
                <tr>
                  <td colSpan="10" className={CRUD_UI.emptyCell}>
                    <div className="flex flex-col items-center gap-2">
                      <FiFileText className="text-5xl text-slate-300" />
                      <p className="text-slate-500 text-sm">
                        Không tìm thấy sync job nào
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedJobs.map((job, idx) => (
                  <tr key={job.id} className={CRUD_UI.tableRow}>
                    {/* Row number */}
                    <td className="px-3 py-2 text-sm text-slate-500 whitespace-nowrap">
                      {(currentPage - 1) * itemsPerPage + idx + 1}
                    </td>

                    {/* Status */}
                    <td className="px-3 py-2 whitespace-nowrap">
                      {getStatusBadge(job.status)}
                    </td>

                    {/* Type */}
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 uppercase">
                        {job.type || '-'}
                      </span>
                    </td>

                    {/* Patient name */}
                    <td className="px-3 py-2 text-sm text-slate-900 font-medium whitespace-nowrap">
                      {job.patient_name || (
                        <span className="text-slate-400 font-normal">-</span>
                      )}
                    </td>

                    {/* Patient ID */}
                    <td className="px-3 py-2 text-sm text-slate-600 whitespace-nowrap">
                      {job.patient_id || '-'}
                    </td>

                    {/* Site */}
                    <td className="px-3 py-2 text-sm text-slate-700 whitespace-nowrap">
                      {job.site_name || (
                        <span className="text-xs text-slate-400">
                          {job.site_id || '-'}
                        </span>
                      )}
                    </td>

                    {/* Started at */}
                    <td className="px-3 py-2 text-sm text-slate-600 whitespace-nowrap">
                      {formatDateTime(job.started_at)}
                    </td>

                    {/* Finished at */}
                    <td className="px-3 py-2 text-sm text-slate-600 whitespace-nowrap">
                      {job.finished_at ? (
                        formatDateTime(job.finished_at)
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>

                    {/* Duration */}
                    <td className="px-3 py-2 text-sm text-slate-600 whitespace-nowrap">
                      {formatDuration(job.started_at, job.finished_at)}
                    </td>

                    {/* Log */}
                    <td className="px-3 py-2 text-sm max-w-[260px]">
                      <LogCell log={job.log} onOpen={setSelectedLog} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {filteredJobs.length > 0 && (
        <div className={CRUD_UI.paginationDock}>
          <Pagination
            currentPage={currentPage}
            totalItems={filteredJobs.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[10, 20, 50, 100]}
          />
        </div>
      )}

      {selectedLog && (
        <div className={CRUD_UI.modalOverlay}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className={CRUD_UI.modalHeader}>
              <h3 className={CRUD_UI.modalTitle}>Chi tiết log đồng bộ</h3>
              <button
                onClick={() => setSelectedLog(null)}
                className={CRUD_UI.modalCloseButton}
                title="Đóng"
              >
                <FiX className="text-lg" />
              </button>
            </div>

            <div className="p-6 overflow-auto min-h-0 flex-1">
              <pre className="p-3 bg-slate-50 border border-slate-200 rounded text-xs text-slate-700 whitespace-pre-wrap break-words">
                {selectedLog.jsonRaw}
              </pre>
            </div>

            <div className={CRUD_UI.modalFooter}>
              <button
                onClick={() => setSelectedLog(null)}
                className={CRUD_UI.secondaryButton}
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncJobs;
